import { useDisclosure } from "@chakra-ui/hooks";
import { Box, Flex } from "@chakra-ui/layout";
import { SlideFade } from "@chakra-ui/transition";
import { saveAs } from "file-saver";
import { Formik } from "formik";
import React, { useState } from "react";
import { PdfApi } from "../../api/PdfApi";
import useComments from "../../hooks/useComments";
import useGetFormTabs from "../../hooks/useGetFormTabs";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { FormSectionStatus } from "../../lib/models/form-section-status.model";
import { FixAndFlipQuoteDetail } from "../../lib/models/quote.model";
import {
  RentalForm,
  RentalPremierForm,
} from "../../lib/models/rental/rental-form.model";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";
import {
  ExternalFixAndFlipFormSchema,
  FixAndFlipForm,
  FixAndFlipFormSchema,
} from "../../lib/models/short-term/short-term-form.model";
import {
  BorrowerInfo,
  DealTerms,
  FixAndFlipQuote,
  LoanSummary,
  LoanTerm,
} from "../../lib/models/short-term/short-term.model";
import {
  getFileName,
} from "../../lib/pdf-generator";
import { QuoteUtils } from "../../lib/quote-utils";
import { PdfGenerationActions } from "../../lib/types/PDFGenerationActions";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { QuoteFormProps } from "../../lib/types/QuoteFormProps";
import QuoteBoxBottom from "./QuoteBoxBottom";
import QuoteBoxTop from "./QuoteBoxTop";
import QuoteFormTabs from "./QuoteFormTabs";

function FixAndFlipQuoteForm({
  quote,
  submitHandler,
  saveHandler,
  saveQuoteMetrics,
  calculateHandler,
  deleteHandler,
  isCalculating,
  isLoading,
}: QuoteFormProps) {
  const quoteContext = useQuote();
  const userContext = useUser();
  const { comments } = useComments();

  const [form] = useState<FixAndFlipForm>(
    quote ? new FixAndFlipForm(quote) : new FixAndFlipForm(null)
  );

  const [tabIndex, setTabIndex] = useState(0);
  const [isPdfLoading, setPdfLoading] = useState<boolean>(false);
  const { state } = quoteContext;
  const { sfProfile, isEmployee } = userContext;
  const { quoteCalculation: calculation, metadata } = state;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const quoteTabs = useGetFormTabs();

  const formExportHandler = (
    action: PdfGenerationActions,
    values: FixAndFlipForm
  ) => {
    Promise.resolve(QuoteUtils.FixAndFlipFormToQuote(values))
      .then((fixAndFlipQuote: FixAndFlipQuote) => {
        if (fixAndFlipQuote) {
          setPdfLoading(true);
          Promise.resolve(generatePdf(fixAndFlipQuote, action))
            .then((res) => {
              const blob = new Blob([res.data], { type: "application/pdf" });
              saveAs(blob, `${getFileName(quote?.quoteName, action)}.pdf`);
            })
            .catch((error) => console.error(error))
            .then(() => {
              setPdfLoading(false);
            });
          Promise.resolve(saveQuoteMetrics("PDF"))
            .then(() => {})
            .catch((error) => console.error(error));
        }
      })
      .catch((error) => console.error(error));
  };

  const formActionHandler = (
    action: QuoteFormActions,
    values: FixAndFlipForm
  ) => {
    const sfValues = state.sfIntegration;
    Promise.resolve(
      QuoteUtils.FixAndFlipFormToQuote({ ...values, ...sfValues })
    )
      .then((fixAndFlipQuote: FixAndFlipQuote) => {
        if (action === "CALCULATE") {
          calculateHandler(fixAndFlipQuote);
        } else if (action === "SAVE") {
          saveHandler(fixAndFlipQuote);
        } else if (action === "SAVE_AS") {
          onOpen();
        } else if (action === "SUBMIT") {
          submitHandler(fixAndFlipQuote);
        } else if (action === "SUBMIT_FOR_REVIEW") {
          submitHandler(fixAndFlipQuote);
        } else if (action === "DELETE") {
          deleteHandler(fixAndFlipQuote);
        }
      })
      .catch((error) => console.error(error));
  };

  const handleSaveAs = (
    values: FixAndFlipForm | RentalForm | RentalPremierForm
  ) => {
    Promise.resolve(QuoteUtils.FixAndFlipFormToQuote(values as FixAndFlipForm))
      .then((fixAndFlipQuote: FixAndFlipQuote) => {
        saveHandler(fixAndFlipQuote, true);
      })
      .catch((error) => console.error(error));
  };

  const generatePdf = async (
    quote: FixAndFlipQuote,
    action: PdfGenerationActions
  ) => {
    const pdfQuote = new FixAndFlipQuoteDetail(
      quote as FixAndFlipQuote,
      calculation as FixAndFlipCalculation
    );
    try {
        return await PdfApi.getStfPdf({
          userProfile: sfProfile,
          metadata,
          action,
          quote: pdfQuote,
          discussionArray: comments,
        });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Formik
      initialValues={form}
      validateOnBlur={true}
      validateOnChange={false}
      validateOnMount={true}
      onSubmit={() => {}}
      onReset={() => {}}
      validationSchema={
        isEmployee ? FixAndFlipFormSchema : ExternalFixAndFlipFormSchema
      }
    >
      <>
        <Flex>
          <Box
            id="form"
            as="form"
            position="relative"
            paddingBottom={14}
            flexShrink={2}
            flex={1}
            maxWidth="full"
            minWidth={0}
          >
            <QuoteBoxTop
              quoteName={form.quoteName}
              formActionHandler={formActionHandler}
              formExportHandler={formExportHandler}
              loadingPdf={isPdfLoading}
              isCalculating={isCalculating}
              quoteTabs={quoteTabs}
              setTabIndex={setTabIndex}
              calculation={calculation}
            />

            <SlideFade in={!isLoading} offsetY="10px">
              <QuoteFormTabs
                quote={quote}
                isCalculating={isCalculating}
                quoteTabs={quoteTabs}
                setTabIndex={setTabIndex}
                tabIndex={tabIndex}
                isFnf={true}
                calculation={calculation}
                formActionHandler={formActionHandler}
                preferredLoanAmt={undefined}
                setPreferredLoanAmt={undefined}
                formStatusSections={formStatusSections}
              />
            </SlideFade>
          </Box>

          <QuoteBoxBottom
            formActionHandler={formActionHandler}
            calculation={calculation}
            onSaveClick={handleSaveAs}
            isOpen={isOpen}
            onClose={onClose}
          />
        </Flex>
      </>
    </Formik>
  );
}

export default FixAndFlipQuoteForm;

const formStatusSections: FormSectionStatus[] = [
  {
    label: "Loan Summary",
    id: "loan-summary",
    fields: Object.keys(new LoanSummary()),
    status: "info",
    value: "loanSummary",
  },
  {
    label: "Borrower Information",
    id: "borrower-information",
    fields: Object.keys(new BorrowerInfo()),
    status: "info",
    value: "borrowerInformation",
    numberOfOptionalFields: 1,
  },
  {
    label: "Loan Terms",
    id: "loan-terms",
    fields: Object.keys(new LoanTerm()),
    status: "info",
    value: "loanTerms",
  },
  {
    label: "Deal Terms",
    id: "deal-terms",
    fields: Object.keys(new DealTerms()),
    status: "info",
    value: "dealTerms",
  },
];
